/**
 * v1.2 Batch 5-A/C/D：投流归因 ROI 矩阵
 *
 * /reports/ad-attribution
 * - 6 维度切换（投放计划 / 关键词 / 创意 / 账户 / 笔记 / 作者）
 * - 时间筛选（今日/本周/本月/本季/自定义）
 * - 列：维度值 / 客户数 / 成单数 / 转化率 / 累计回款 / 单客均价 /
 *      投放花费 / ROI / CAC / 转化成本 / L1-L7 漏斗 / 7 日趋势
 * - 行展开：客户列表 drawer
 * - CSV 导出
 * - 右侧 AI 智能洞察侧栏（5-D）
 */
import { useState, useMemo } from 'react';
import { Select, DatePicker, Table, Drawer, message, Tabs } from 'antd';
import { useQuery } from '@tanstack/react-query';
import dayjs, { Dayjs } from 'dayjs';
import { $ } from '../../lib/api';
import { PageHeader, HomeCard, Pill, Btn, TableWrap } from '../../components/ui';

const { RangePicker } = DatePicker;

const DIMENSIONS = [
  { value: 'campaign', label: '投放计划' },
  { value: 'unit', label: '广告组' },
  { value: 'creativity', label: '创意' },
  { value: 'account', label: '投放账户' },
  { value: 'note', label: '归因笔记' },
  { value: 'author', label: '笔记作者' },
];

const PRESETS = [
  { value: 'today', label: '今日' },
  { value: 'week', label: '本周' },
  { value: 'month', label: '本月' },
  { value: 'quarter', label: '本季' },
  { value: 'custom', label: '自定义' },
];

function presetRange(p: string): [Dayjs, Dayjs] | null {
  const now = dayjs();
  switch (p) {
    case 'today':    return [now.startOf('day'), now];
    case 'week':     return [now.startOf('week'), now];
    case 'month':    return [now.startOf('month'), now];
    case 'quarter':  return [now.startOf('quarter' as any), now];
    default: return null;
  }
}

export function AdAttributionPage() {
  const [dimension, setDimension] = useState('campaign');
  const [preset, setPreset] = useState('month');
  const [customRange, setCustomRange] = useState<[Dayjs, Dayjs] | null>(null);
  const [drawerValue, setDrawerValue] = useState<string | null>(null);

  const range = preset === 'custom' ? customRange : presetRange(preset);
  const startDate = range ? range[0].format('YYYY-MM-DD') : dayjs().subtract(30, 'day').format('YYYY-MM-DD');
  const endDate = range ? range[1].format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD');

  const { data, isLoading } = useQuery<any>({
    queryKey: ['ad-aggregate', dimension, startDate, endDate],
    queryFn: () => $.get('/reports/ad/aggregate', { dimension, startDate, endDate, limit: 100 }),
  });

  // v1.2-rc2 P1-4：当维度无数据时显示空态文案而非红 toast
  const isEmpty = !isLoading && (data?.rows?.length ?? 0) === 0;

  const { data: insight } = useQuery<any>({
    queryKey: ['ad-insight-weekly'],
    queryFn: () => $.get('/reports/ad/insight/weekly'),
    staleTime: 60 * 60_000, // 1h
  });

  const { data: drilldown } = useQuery<any[]>({
    queryKey: ['ad-drilldown', dimension, drawerValue, startDate, endDate],
    queryFn: () => $.get('/reports/ad/drilldown', {
      dimension, value: drawerValue, startDate, endDate, limit: 100,
    }),
    enabled: !!drawerValue,
  });

  const exportCsv = () => {
    if (!data?.rows?.length) { message.warning('暂无数据可导出'); return; }
    const headers = ['维度值', '客户数', '成单数', '转化率%', '累计回款', '单客均价', '花费', 'ROI', 'CAC', '转化成本'];
    const rows = data.rows.map((r: any) => [
      r.dimensionValue, r.customerCount, r.dealCount,
      (r.conversionRate * 100).toFixed(2),
      r.totalRevenue.toFixed(2), r.avgRevenue.toFixed(2),
      r.totalSpend.toFixed(2), r.roi.toFixed(2),
      r.cac.toFixed(2), r.conversionCost.toFixed(2),
    ]);
    const csv = [headers, ...rows].map((line: any[]) =>
      line.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')
    ).join('\n');
    const blob = new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ad-attribution-${dimension}-${startDate}-${endDate}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <PageHeader
        breadcrumb={['投流营销', '投放 ROI 矩阵']}
        title="投放 ROI 矩阵"
        subtitle="6 维度归因聚合 + 花费 / ROI / CAC / L1-L7 漏斗"
        actions={<Btn kind="default" onClick={exportCsv}>导出 CSV</Btn>}
      />
      <div className="work-content" style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 16, alignItems: 'flex-start' }}>
        <div>
          <HomeCard title="筛选" action={
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <Select value={dimension} onChange={setDimension} style={{ width: 140 }}
                      options={DIMENSIONS} />
              <Select value={preset} onChange={setPreset} style={{ width: 100 }}
                      options={PRESETS} />
              {preset === 'custom' && (
                <RangePicker value={customRange as any}
                             onChange={(v: any) => setCustomRange(v)}
                             allowClear={false} />
              )}
            </div>
          }>
            {/* totals */}
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 12, fontSize: 13 }}>
              <KV label="客户数" value={data?.totals?.totalCustomers ?? 0} />
              <KV label="成单数" value={data?.totals?.totalDeals ?? 0} />
              <KV label="累计回款" value={`¥${(data?.totals?.totalRevenue ?? 0).toLocaleString()}`} />
              <KV label="累计花费" value={`¥${(data?.totals?.totalSpend ?? 0).toLocaleString()}`} />
              <KV label="整体 ROI"
                  value={`${(data?.totals?.roi ?? 0).toFixed(2)}x`}
                  color={data?.totals?.roi >= 1 ? 'success' : 'warning'} />
            </div>
          </HomeCard>

          <HomeCard title={`明细（${data?.rows?.length ?? 0}）`}>
            {isEmpty && (
              <div style={{
                padding: '24px 16px',
                color: 'var(--text-3, #8F959E)', fontSize: 13, textAlign: 'center',
                background: 'var(--bg-2, #F4F5F7)', borderRadius: 8, marginBottom: 12,
              }}>
                该维度暂无数据，请尝试录入投放花费或切换其他维度
              </div>
            )}
            <TableWrap>
              <Table
                rowKey="dimensionValue"
                loading={isLoading}
                dataSource={data?.rows ?? []}
                pagination={{ pageSize: 30 }}
                size="middle"
                onRow={(r: any) => ({
                  onClick: () => setDrawerValue(r.dimensionValue),
                  style: { cursor: 'pointer' },
                })}
                scroll={{ x: 1400 }}
                columns={[
                  { title: DIMENSIONS.find((d) => d.value === dimension)?.label ?? '维度',
                    dataIndex: 'dimensionValue', width: 200, fixed: 'left' as any },
                  { title: '客户', dataIndex: 'customerCount', width: 70, align: 'right' as any },
                  { title: '成单', dataIndex: 'dealCount', width: 70, align: 'right' as any },
                  { title: '转化率', dataIndex: 'conversionRate', width: 90, align: 'right' as any,
                    render: (v: number) => `${(v * 100).toFixed(1)}%` },
                  { title: '回款 ¥', dataIndex: 'totalRevenue', width: 110, align: 'right' as any,
                    render: (v: number) => v.toLocaleString() },
                  { title: '均价 ¥', dataIndex: 'avgRevenue', width: 100, align: 'right' as any,
                    render: (v: number) => v.toFixed(0) },
                  { title: '花费 ¥', dataIndex: 'totalSpend', width: 100, align: 'right' as any,
                    render: (v: number) => (
                      <span style={{ color: v > 0 ? 'inherit' : 'var(--text-3, #8F959E)' }}>
                        {v > 0 ? v.toLocaleString() : '未录'}
                      </span>
                    ),
                  },
                  { title: 'ROI', dataIndex: 'roi', width: 80, align: 'right' as any,
                    render: (v: number, r: any) => r.totalSpend > 0 ? (
                      <Pill tone={v >= 2 ? 'success' : v >= 1 ? 'primary' : 'warning'}>
                        {v.toFixed(2)}x
                      </Pill>
                    ) : '—',
                  },
                  { title: 'CAC', dataIndex: 'cac', width: 90, align: 'right' as any,
                    render: (v: number, r: any) => r.totalSpend > 0 ? `¥${v.toFixed(0)}` : '—' },
                  { title: '转化成本', dataIndex: 'conversionCost', width: 100, align: 'right' as any,
                    render: (v: number, r: any) => r.totalSpend > 0 && r.dealCount > 0 ? `¥${v.toFixed(0)}` : '—' },
                  { title: 'L1-L7 漏斗', width: 280,
                    render: (_v: any, r: any) => (
                      <FunnelMini steps={[r.funnelL1, r.funnelL2, r.funnelL3, r.funnelL4, r.funnelL5, r.funnelL6, r.funnelL7]} />
                    ),
                  },
                ]}
              />
            </TableWrap>
          </HomeCard>
        </div>

        {/* 右侧 AI 洞察侧栏 */}
        <div style={{ position: 'sticky', top: 16 }}>
          <HomeCard title="🤖 本周 AI 洞察">
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 12, color: 'var(--text-3, #8F959E)', marginBottom: 6 }}>Top 5 高效计划</div>
              {(insight?.topPerformers ?? []).map((p: any, i: number) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, padding: '4px 0' }}>
                  <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {p.dimensionValue}
                  </span>
                  <Pill tone="success">{p.roi.toFixed(2)}x</Pill>
                </div>
              ))}
            </div>
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 12, color: 'var(--text-3, #8F959E)', marginBottom: 6 }}>低效计划</div>
              {(insight?.bottomPerformers ?? []).map((p: any, i: number) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, padding: '4px 0' }}>
                  <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {p.dimensionValue}
                  </span>
                  <Pill tone="warning">{p.roi.toFixed(2)}x</Pill>
                </div>
              ))}
            </div>
            <div style={{
              padding: 12, background: 'var(--bg-2, #F4F5F7)', borderRadius: 8,
              fontSize: 12, color: 'var(--text-1, #1F2329)', lineHeight: 1.6,
            }}>
              {insight?.aiComments ?? 'AI 评语加载中…'}
            </div>
          </HomeCard>
        </div>
      </div>

      <Drawer
        open={!!drawerValue}
        onClose={() => setDrawerValue(null)}
        title={`下钻 — ${drawerValue}`}
        width={680}
      >
        {drilldown && (
          <Table
            rowKey="id"
            dataSource={drilldown}
            pagination={{ pageSize: 20 }}
            size="small"
            columns={[
              { title: '客户', render: (_v: any, r: any) => r.real_name ?? r.nickname ?? '—' },
              { title: '阶段', dataIndex: 'current_stage', width: 80 },
              { title: '回款', dataIndex: 'total_paid', width: 100,
                render: (v: any) => v ? `¥${Number(v).toLocaleString()}` : '—' },
            ]}
          />
        )}
      </Drawer>
    </>
  );
}

function KV({ label, value, color }: { label: string; value: any; color?: 'success' | 'warning' }) {
  return (
    <div>
      <div style={{ fontSize: 11, color: 'var(--text-3, #8F959E)' }}>{label}</div>
      <div style={{
        fontSize: 16, fontWeight: 500,
        color: color === 'success' ? 'var(--success, #19BC9C)'
             : color === 'warning' ? 'var(--warning, #FFC600)'
             : 'var(--text-1, #1F2329)',
      }}>
        {value}
      </div>
    </div>
  );
}

function FunnelMini({ steps }: { steps: number[] }) {
  const max = Math.max(...steps, 1);
  return (
    <div style={{ display: 'flex', gap: 2, alignItems: 'flex-end', height: 24 }}>
      {steps.map((s, i) => (
        <div key={i}
             title={`L${i + 1}: ${s}`}
             style={{
               flex: 1,
               background: 'var(--primary, #3370FF)',
               opacity: 0.4 + (s / max) * 0.6,
               height: `${Math.max(2, (s / max) * 100)}%`,
               minWidth: 6,
               borderRadius: 2,
             }}>
        </div>
      ))}
    </div>
  );
}
